<script setup lang="ts">
import { computed } from 'vue';
import { fallbackOnError } from '@/utils/imageFallback';
import { avatarPlaceholderUrl } from '@/utils/avatarPlaceholder';
import FlowSignMark from '@/components/CharacterToken/FlowSignMark.vue';

interface Props {
  avatarUrl?: string;
  statsIncrementIcon: boolean;
  /** Character is Signed with Flow — under game master authority. */
  signed?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  avatarUrl: '',
  statsIncrementIcon: false,
  signed: false
});

/* The HUD avatar is handed a URL and nothing else — no biography, so no gender —
   and the default it used to carry was a stranger's screenshot on a third-party
   CDN. Both the empty case and the broken-file fallback are now `other.png`, the
   same file the server assigns when it knows no better. */
const avatarPlaceholder = avatarPlaceholderUrl();

const resolvedAvatarUrl = computed(() => props.avatarUrl || avatarPlaceholder);
</script>

<template>
  <div class="rpg-avatar" title="Character Info">
    <div class="rpg-avatar__wrapper">
      <img
          :src="resolvedAvatarUrl"
          alt="Character Avatar"
          class="rpg-avatar__image"
          sizes="content"
          @error="fallbackOnError($event, avatarPlaceholder)"
      />
      <div
        v-if="props.statsIncrementIcon"
        class="rpg-avatar__increment-overlay"
        title="Stats can be incremented"
      >
        <span class="rpg-avatar__increment-symbol">+</span>
      </div>
      <FlowSignMark v-if="props.signed" :size="24"/>
    </div>
  </div>
</template>

<style scoped>
.rpg-avatar {
  width: 10rem;
  height: 15rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  background: url("@/assets/rpg_bars/rpg_avatar_border.png") no-repeat center;
  background-size: cover;

  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;

}

.rpg-avatar__wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: start;
  background-size: cover;
  mask: url("@/assets/rpg_bars/rpg_avatar_border_mask_invented.png") no-repeat center;
  mask-size: cover;
}

.rpg-avatar__image {
  width: 65%;
  height: 60%;
  object-fit: cover;
  object-position: center;
  position: absolute;
  top: 10%;
}

.rpg-avatar__increment-overlay {
  position: absolute;
  top: 10%;
  left: 17.5%;
  width: 65%;
  height: 60%;
  background: rgba(255, 215, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: gentle-pulse 1.5s ease-in-out infinite;
  z-index: 5;
}

.rpg-avatar__increment-symbol {
  font-size: 32px;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.9);
  text-shadow:
    0 2px 4px rgba(0, 0, 0, 0.8),
    0 0 8px rgba(255, 215, 0, 0.6);
  filter: drop-shadow(0 0 4px rgba(255, 215, 0, 0.5));
}

@keyframes gentle-pulse {
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.5;
  }
}

</style>